import React, { useState } from 'react';
import Nav from '../../components/Nav';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faDownload, faCopy, faVolumeUp, faLanguage } from '@fortawesome/free-solid-svg-icons';
import { audioEnhancer, audioConvert } from '../../services/apiService';

interface TextEnhancerProps {
  setError: (error: string | null) => void;
  setIsError: (isError: boolean) => void;
}

const TextEnhancer: React.FC<TextEnhancerProps> = ({ setError, setIsError }) => {
  const [text, setText] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [enhancedTextVersion, setEnhancedTextVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMes, setLoadingMes] = useState('');

  const role = localStorage.getItem('role')?.trim();

  // All languages fully supported by VoiceMaker
  const languages = [
    { code: 'English', name: 'English', flag: '🇺🇸', hasAudio: true },
    { code: 'Thai', name: 'Thai', flag: '🇹🇭', hasAudio: true },
    { code: 'Vietnamese', name: 'Vietnamese', flag: '🇻🇳', hasAudio: true }
  ];
  
 
  if (!role || !['1', '7'].includes(role)) {
    return (
      <div className="text-enhancer-container">
        <Nav />
        <div className="flex items-center justify-center min-h-screen bg-gray-50 -mt-16">
          <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faVolumeUp} className="text-2xl text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-6">You don't have permission to access the Text Enhancer feature.</p>
            <button 
              onClick={() => window.history.back()} 
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleGenerateAudio = async () => {
    if (!text.trim()) {
      alert('Enter text for audio conversion');
      return;
    }
    
    setLoading(true);
    setLoadingMes('enhancing');
    
    try {
      const result = await audioEnhancer(text);
      if (result.data.status && result.data.status === 'success') {
        setEnhancedTextVersion(result.data.response.choices[0].message.content);
        // No popup - just show the enhanced text in the UI
      } else {
        alert('Something went wrong in audio enhancement');
      }
    } catch (error) {
      setIsError(true);
      if (error instanceof Error) {
        setError('Error while text enhancement');
      } else {
        setError("An unexpected error occurred while text enhancement");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setEnhancedTextVersion('');
    setAudioUrl('');
    // Keep the text so user can modify and re-enhance
  };

  const handleClearAll = () => {
    setText('');
    setEnhancedTextVersion('');
    setAudioUrl('');
  };

  const convertToAudio = async (textToConvert: string) => {
    setLoading(true);
    setLoadingMes('converting');
    setAudioUrl('');
    
    try {
      let languageCode = selectedLanguage;
      
     
      if (selectedLanguage === 'Vietnamese') {
        languageCode = 'Veitnamese'; 
      }
      
      console.log('Converting to audio:', textToConvert, 'Language:', languageCode);
      const result = await audioConvert(textToConvert, languageCode);
      const url = URL.createObjectURL(result.data);
      setAudioUrl(url);
      console.log('Audio conversion successful!');
    } catch (error) {
      console.error('Audio conversion error:', error);
      setIsError(true);
      setError('Error while audio conversion');
    } finally {
      setLoading(false);
    }
  };

  const handleUseOriginalText = () => {
    convertToAudio(text);
  };

  const handleUseEnhancedText = () => {
    setText(enhancedTextVersion);
    convertToAudio(enhancedTextVersion);
  };

  const handlePlayPause = () => {
    const audioElement = document.getElementById('generated-audio') as HTMLAudioElement;
    if (audioElement) {
      if (isPlaying) {
        audioElement.pause();
      } else {
        audioElement.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleDownload = () => {
    if (audioUrl) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `text-to-speech-${selectedLanguage}.wav`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="text-enhancer-container">
      <Nav />
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              <FontAwesomeIcon icon={faVolumeUp} className="mr-3 text-blue-600" />
              Text to Speech Converter
            </h1>
 
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-lg p-6 border-0">
                <div className="mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-2">Text Processing</h2>
                  <p className="text-gray-600 text-sm">Enter your text and enhance it, then convert to speech</p>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Original Text</label>
                      <button
                        onClick={copyToClipboard}
                        className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                        title="Copy text"
                      >
                        <FontAwesomeIcon icon={faCopy} className="text-xs" />
                      </button>
                    </div>
                    <div className="relative">
                      <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="w-full h-52 p-4 border-2 border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="Enter your text here (English, Thai, or Vietnamese)..."
                      />
                      <div className="absolute bottom-3 left-3 text-xs text-gray-400">
                        {text.length} characters
                      </div>
                    </div>
                    
                    <div className="flex justify-center">
                      {!enhancedTextVersion ? (
                        <button
                          onClick={handleGenerateAudio}
                          disabled={loading || !text.trim()}
                          className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-400 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:cursor-not-allowed transform hover:scale-105 disabled:transform-none"
                        >
                          {loading && loadingMes === 'enhancing' ? (
                            <>
                              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Enhancing ...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faVolumeUp} className="mr-2" />
                              Enhance
                            </>
                          )}
                        </button>
                      ) : (
                        <div className="flex space-x-3">
                          <button
                            onClick={handleUseOriginalText}
                            disabled={loading}
                            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-400 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                          >
                            {loading && loadingMes === 'converting' ? (
                              <>
                                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Converting...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faPlay} className="mr-2" />
                                Use Original
                              </>
                            )}
                          </button>
                          
                          <button
                            onClick={handleClearAll}
                            className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                            title="Clear all and start fresh"
                          >
                            New
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

            
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">
                        Enhanced Text
                      </label>
                      {enhancedTextVersion && (
                        <div className="flex items-center text-emerald-600">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse mr-2"></div>
                          <span className="text-xs font-medium">Ready</span>
                        </div>
                      )}
                    </div>
                    <div className={`relative transition-all duration-300 ${enhancedTextVersion ? 'transform scale-100' : 'transform scale-95 opacity-75'}`}>
                      <div className={`w-full h-52 p-4 rounded-xl border-2 transition-all duration-300 ${
                        enhancedTextVersion 
                          ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-green-50 shadow-lg' 
                          : 'border-gray-200 bg-gray-50'
                      }`}>
                        {enhancedTextVersion ? (
                          <div className="h-full overflow-y-auto">
                            <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{enhancedTextVersion}</p>
                          </div>
                        ) : (
                          <div className="h-full flex items-center justify-center">
                            <div className="text-center text-gray-400">
                              <FontAwesomeIcon icon={faVolumeUp} className="text-3xl mb-3 opacity-50" />
                              <p className="text-sm">AI enhanced text will appear here</p>
                              <p className="text-xs mt-1">Better grammar, clarity, and flow</p>
                            </div>
                          </div>
                        )}
                      </div>
                      {enhancedTextVersion && (
                        <div className="absolute bottom-3 left-3 text-xs text-emerald-600 bg-white px-2 py-1 rounded-full">
                          {enhancedTextVersion.length} characters
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-center">
                      {enhancedTextVersion && (
                        <button
                          onClick={handleUseEnhancedText}
                          disabled={loading}
                          className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-400 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                          {loading && loadingMes === 'converting' ? (
                            <>
                              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Converting...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faPlay} className="mr-2" />
                              Use Enhanced
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            
              <div className="bg-white rounded-lg shadow-lg p-6 mt-6 border-0">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-800">Audio Player</h2>
                    <p className="text-gray-600 text-sm mt-1">Listen to your generated speech</p>
                  </div>
                  {audioUrl && (
                    <div className="flex items-center text-green-600">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-2"></div>
                      <span className="text-sm font-medium">Audio Ready</span>
                    </div>
                  )}
                </div>
                
                {audioUrl ? (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 text-center border-2 border-gray-200">
                    <audio 
                      id="generated-audio"
                      controls 
                      className="w-full mb-6 rounded-lg shadow-md"
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onEnded={() => setIsPlaying(false)}
                    >
                      <source src={audioUrl} type="audio/wav" />
                      Your browser does not support the audio element.
                    </audio>
                    
                    <div className="flex justify-center space-x-4">
                      <button
                        onClick={handlePlayPause}
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white p-4 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                        title="Play/Pause"
                      >
                        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} className="text-lg" />
                      </button>
                      
                      <button
                        onClick={handleDownload}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white p-4 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                        title="Download Audio"
                      >
                        <FontAwesomeIcon icon={faDownload} className="text-lg" />
                      </button>
                    </div>
                    
                    <p className="text-gray-600 text-sm mt-4">Audio ready for download</p>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-12 text-center border-2 border-dashed border-gray-300">
                    <div className="text-gray-400 mb-4">
                      <FontAwesomeIcon icon={faVolumeUp} className="text-6xl opacity-50" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-600 mb-2">No Audio Generated Yet</h3>
                    <p className="text-gray-500 text-sm">
                      Enter your text, enhance it, and click "Convert" to generate speech
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Language Selection */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-lg p-6 border-0">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={faLanguage} className="text-2xl text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Language</h3>
                  <p className="text-gray-600 text-sm">Select your text language</p>
                </div>
                
                <div className="space-y-4">
                  {languages.map((lang) => (
                    <label
                      key={lang.code}
                      className={`block cursor-pointer transition-all duration-200 transform hover:scale-105 ${
                        selectedLanguage === lang.code
                          ? 'bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-500 shadow-lg'
                          : 'bg-gray-50 border-2 border-gray-200 hover:border-blue-300'
                      } rounded-xl p-4`}
                    >
                      <input
                        type="radio"
                        name="language"
                        value={lang.code}
                        checked={selectedLanguage === lang.code}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className="sr-only"
                        disabled={loading}
                      />
                      <div className="flex items-center space-x-3">
                        <span className="text-3xl">{lang.flag}</span>
                        <div className="flex-1">
                          <div className={`font-medium ${
                            selectedLanguage === lang.code ? 'text-blue-800' : 'text-gray-800'
                          }`}>
                            {lang.name}
                          </div>
                          {selectedLanguage === lang.code && (
                            <div className="text-blue-600 text-xs mt-1 flex items-center">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                              Selected
                            </div>
                          )}
                        </div>
                        {selectedLanguage === lang.code && (
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div style={{position:'fixed',top:'0',bottom:'0',width:'100%',display:'flex',alignItems:'center',justifyContent:'center',backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000}}>
          <div style={{background:'white',border:' 2px solid #0050C8'}} className="flex items-center rounded px-7 py-3">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 me-2"></div>
            {loadingMes === 'enhancing' ? (
              <p className="text-blue-700">Enhancing text please wait...</p>
            ) : loadingMes === 'converting' ? (
              <p className="text-blue-700">Converting please wait...</p>
            ) : (
              <p className="text-blue-700">Loading please wait...</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TextEnhancer;